import { createSignal } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { Column } from "@lightningtv/solid-ui";
import { fontFamily, isRTL, t } from "../../../translations/configuration";
import { clearMovieFavorites } from "../../../api/providers/moviesFavorites";
import { clearSeriesFavorites } from "../../../api/providers/seriesFavorites";

type ClearedState = {
  favMovies: boolean;
  favSeries: boolean;
  favChannels: boolean;
  continueWatchingMovies: boolean;
  continueWatchingSeries: boolean;
};

type StorageAndDataProps = {
  x?: number;
  y?: number;
  onLeftFromOption?: () => void;
  onRightFromOption?: () => void;
  favChannelsRef?: (el: any) => void;
  favMoviesRef?: (el: any) => void;
  favSeriesRef?: (el: any) => void;
  continueWatchingMoviesRef?: (el: any) => void;
  continueWatchingSeriesRef?: (el: any) => void;
  clearAllRef?: (el: any) => void;
};

const StorageAndData = (props: StorageAndDataProps) => {
  const [focusedItem, setFocusedItem] = createSignal<string | null>(null);
  const [cleared, setCleared] = createSignal<ClearedState>({
    favMovies: false,
    favSeries: false,
    favChannels: false,
    continueWatchingMovies: false,
    continueWatchingSeries: false,
  });
  const [showConfirmModal, setShowConfirmModal] = createSignal(false);
  const [pendingAction, setPendingAction] = createSignal<(() => void) | null>(null);
  const [modalTitle, setModalTitle] = createSignal("");
  const [modalMessage, setModalMessage] = createSignal("");

  let confirmOkRef: any;
  let confirmCancelRef: any;

  const confirmAndClear = (title: string, message: string, onConfirm: () => void) => {
    setModalTitle(title);
    setModalMessage(message);
    setPendingAction(() => onConfirm);
    setShowConfirmModal(true);
    setTimeout(() => confirmCancelRef?.setFocus(), 100);
  };

  const handleClearFavMovies = () =>
    confirmAndClear(
      t("clear_fav_movies_title"),
      t("clear_fav_movies_message"),
      () => {
        clearMovieFavorites();
        setCleared((prev) => ({ ...prev, favMovies: true }));
      }
    );

  const handleClearFavSeries = () =>
    confirmAndClear(
      t("clear_fav_series_title"),
      t("clear_fav_series_message"),
      () => {
        clearSeriesFavorites();
        setCleared((prev) => ({ ...prev, favSeries: true }));
      }
    );

  const handleClearFavChannels = () =>
    confirmAndClear(
      t("clear_fav_channels_title"),
      t("clear_fav_channels_message"),
      () => {
        localStorage.removeItem("@favoriteChannels");
        setCleared((prev) => ({ ...prev, favChannels: true }));
      }
    );

  const handleClearContinueWatchingMovies = () =>
    confirmAndClear(
      t("clear_continue_watching_title"),
      t("clear_continue_watching_message"),
      () => {
        localStorage.removeItem("movies_cw_adjusted");
        setCleared((prev) => ({ ...prev, continueWatchingMovies: true }));
      }
    );

  const handleClearContinueWatchingSeries = () =>
    confirmAndClear(
      t("clear_continue_watching_title"),
      t("clear_continue_watching_message"),
      () => {
        localStorage.removeItem("series_continue_watching");
        setCleared((prev) => ({ ...prev, continueWatchingSeries: true }));
      }
    );

  const handleClearAll = () =>
    confirmAndClear(
      t("clear_all_title"),
      t("clear_all_message"),
      () => {
        clearMovieFavorites();
        clearSeriesFavorites();
        localStorage.removeItem("@favoriteChannels");
        localStorage.removeItem("vod_continue_watching");
        localStorage.removeItem("series_continue_watching");
        setCleared({
          favMovies: true,
          favSeries: true,
          favChannels: true,
          continueWatchingMovies: true,
          continueWatchingSeries: true,
        });
      }
    );

  const allCleared = () => Object.values(cleared()).every(Boolean);

  const items = () => [
    {
      key: "favChannels",
      label: t("clear_fav_channels_title"),
      subtitle: t("clear_fav_channels_subtitle"),
      onEnter: handleClearFavChannels,
    },
    {
      key: "favMovies",
      label: t("clear_fav_movies_title"),
      subtitle: t("clear_fav_movies_subtitle"),
      onEnter: handleClearFavMovies,
    },
    {
      key: "favSeries",
      label: t("clear_fav_series_title"),
      subtitle: t("clear_fav_series_subtitle"),
      onEnter: handleClearFavSeries,
    },
    {
      key: "continueWatchingMovies",
      label: t("clear_continue_watching_movies_title"),
      subtitle: t("clear_continue_watching_movies_subtitle"),
      onEnter: handleClearContinueWatchingMovies,
    },
    {
      key: "continueWatchingSeries",
      label: t("clear_continue_watching_series_title"),
      subtitle: t("clear_continue_watching_series_subtitle"),
      onEnter: handleClearContinueWatchingSeries,
    },
  ];

  const ROW_HEIGHT = 105;
  const refs: Record<string, any> = {};

  const refPropMap: Record<string, ((el: any) => void) | undefined> = {
    favChannels: props.favChannelsRef,
    favMovies: props.favMoviesRef,
    favSeries: props.favSeriesRef,
    continueWatchingMovies: props.continueWatchingMoviesRef,
    continueWatchingSeries: props.continueWatchingSeriesRef,
  };

  const favItems = () => items().slice(0, 3);
  const historyItems = () => items().slice(3);

  const renderRow = (
    item: ReturnType<typeof items>[number],
    icon: string,
  ) => {
    const isCleared = () => cleared()[item.key as keyof ClearedState];
    const isFocused = () => focusedItem() === item.key;
    return (
      <View
        ref={(el) => { refs[item.key] = el; refPropMap[item.key]?.(el); }}
        width={880}
        height={ROW_HEIGHT}
        borderRadius={10}
        style={{
          color: hexColor("#00000000"),
          border: { width: 1, color: hexColor("#374151") },
          focus: {
            color: hexColor("#1e3a5f68"),
            border: { width: 2, color: hexColor("#3b82f6") },
          },
          transition: { color: { duration: 150 } },
        }}
        onFocus={() => setFocusedItem(item.key)}
        onBlur={() => setFocusedItem(null)}
        onEnter={item.onEnter}
        onLeft={() => props.onLeftFromOption?.()}
        onRight={() => props.onRightFromOption?.()}
      >
        {/* Accent bar — mirrors in RTL */}
        <View x={isRTL() ? 876 : 0} y={0} width={4} height={ROW_HEIGHT}
          style={{ color: isFocused() ? hexColor("#3b82f6") : hexColor("#00000000") }}
        />

        {/* Text block */}
        <View x={isRTL() ? 10 : 30} y={30}>
          <Text
            width={780} contain="width" textAlign={isRTL() ? "right" : "left"}
            style={{ fontSize: 25, color: hexColor("#e5e7eb"), fontFamily: fontFamily() }}>
            {item.label}
          </Text>
          <Text y={28}
            width={780} contain="width" textAlign={isRTL() ? "right" : "left"}
            style={{
              fontSize: 18,
              color: isCleared() ? hexColor("#22c55e") : hexColor("#9ca3af"),
              fontFamily: fontFamily(),
            }}>
            {isCleared() ? t("cleared_label") : item.subtitle}
          </Text>
        </View>

        {/* Chevron — mirrors in RTL */}
        <Text x={isRTL() ? 10 : 840} y={24}
          style={{
            fontSize: 26,
            color: isCleared() ? hexColor("#22c55e") : isFocused() ? hexColor("#3b82f6") : hexColor("#6b7280"),
            fontFamily: fontFamily(),
          }}>
          {isCleared() ? "✓" : isRTL() ? "‹" : "›"}
        </Text>
      </View>
    );
  };

  return (
    <View x={props.x ?? 600} y={100} height={610} width={920} clipping>

      {/* Title */}
      <Text
        width={880} contain="width" textAlign={isRTL() ? "right" : "left"}
        style={{ fontSize: 32, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
        {t("section_title")}
      </Text>

      {/* Subtitle */}
      <Text y={46}
        textAlign={isRTL() ? "right" : "left"}
        style={{ fontSize: 20, color: hexColor("#9ca3af"), fontFamily: fontFamily(), width: 880, contain: "width" }}>
        {t("section_subtitle")}
      </Text>

      {/* Scrollable Column */}
      <View y={86} height={814} width={880} clipping>
        <Column width={880} scroll="always" gap={10} onLeft={() => props.onLeftFromOption?.()} onRight={() => props.onRightFromOption?.()}>

          {/* Favourites label */}
          <View height={28} width={880} skipFocus>
            <Text
              width={880} contain="width" textAlign={isRTL() ? "right" : "left"}
              style={{ fontSize: 25, color: hexColor("#6b7280"), fontFamily: fontFamily(), fontWeight: isRTL() ? "normal" : "bold" }}>
              {t("favourites_label")}
            </Text>
          </View>

          {favItems().map((item) => renderRow(item, "★"))}

          {/* Continue Watching label */}
          <View height={28} width={880} skipFocus>
            <Text
              width={880} contain="width" textAlign={isRTL() ? "right" : "left"}
              style={{ fontSize: 25, color: hexColor("#6b7280"), fontFamily: fontFamily(), fontWeight: isRTL() ? "normal" : "bold" }}>
              {t("history_label")}
            </Text>
          </View>

          {historyItems().map((item) => renderRow(item, "▶"))}

          {/* Clear All Button */}
          <View
            ref={(el) => { refs["clearAll"] = el; props.clearAllRef?.(el); }}
            width={880}
            height={64}
            borderRadius={10}
            style={{
              color: hexColor("#00000000"),
              border: { width: 1, color: hexColor("#ef444450") },
              focus: { color: hexColor("#ef444414"), border: { width: 2, color: hexColor("#ef444499") } },
            }}
            onFocus={() => setFocusedItem("clearAll")}
            onBlur={() => setFocusedItem(null)}
            onEnter={handleClearAll}
            onLeft={() => props.onLeftFromOption?.()}
            onRight={() => props.onRightFromOption?.()}
          >
            <Text x={0} y={18}
              width={880} contain="width" textAlign={isRTL() ? "right" : "left"}
              style={{
                fontSize: 22,
                color: allCleared() ? hexColor("#6b7280") : hexColor("#ef4444"),
                fontWeight: isRTL() ? "normal" : "bold",
                fontFamily: fontFamily(),
              }}>
              {allCleared() ? t("all_cleared_label") : t("clear_all_title")}
            </Text>
          </View>

        </Column>
      </View>

      {/* Confirmation Modal */}
      {showConfirmModal() && (
        <View
          x={-200}
          y={-100}
          width={1920}
          height={1080}
          style={{ color: hexColor("#00000099"), zIndex: 999 }}
        >
          <View
            x={260}
            y={260}
            width={820}
            height={320}
            borderRadius={20}
            style={{ color: hexColor("#1e2330") }}
          >
            <Text
              x={40} y={40}
              width={740} contain="width" textAlign={isRTL() ? "right" : "left"}
              style={{
                fontSize: 30,
                color: hexColor("#f0f6fc"),
                fontWeight: isRTL() ? "normal" : "bold",
                fontFamily: fontFamily(),
              }}
            >
              {modalTitle()}
            </Text>
            <Text
              x={40} y={90}
              textAlign={isRTL() ? "right" : "left"}
              style={{
                fontSize: 25,
                color: hexColor("#9ca3af"),
                fontFamily: fontFamily(),
                width: 740,
                contain: "width",
              }}
            >
              {modalMessage()}
            </Text>

            {/* Buttons */}
            <View
              ref={(el) => { confirmCancelRef = el; }}
              x={250} y={230}
              width={140} height={50}
              borderRadius={10}
              style={{
                color: hexColor("#374151"),
                focus: { color: hexColor("#4b5563") },
              }}
              onRight={() => { if (!isRTL()) confirmOkRef?.setFocus(); }}
              onLeft={() => { if (isRTL()) confirmOkRef?.setFocus(); }}
              onEnter={() => {
                setShowConfirmModal(false);
                setPendingAction(null);
                setTimeout(() => refs["favChannels"]?.setFocus(), 100);
              }}
            >
              <Text x={0} y={16}
                width={140} contain="width" textAlign="center"
                style={{ fontSize: 22, color: hexColor("#ffffff"), fontFamily: fontFamily() }}>
                {t("remove_cancel_label")}
              </Text>
            </View>

            <View
              ref={(el) => { confirmOkRef = el; }}
              x={430} y={230}
              width={140} height={50}
              borderRadius={10}
              style={{
                color: hexColor("#7f1d1d"),
                focus: { color: hexColor("#ef4444") },
              }}
              onLeft={() => { if (!isRTL()) confirmCancelRef?.setFocus(); }}
              onRight={() => { if (isRTL()) confirmCancelRef?.setFocus(); }}
              onEnter={() => {
                const action = pendingAction();
                if (action) action();
                setShowConfirmModal(false);
                setPendingAction(null);
                setTimeout(() => refs["favChannels"]?.setFocus(), 100);
              }}
            >
              <Text x={0} y={16}
                width={140} contain="width" textAlign="center"
                style={{ fontSize: 22, color: hexColor("#ffffff"), fontFamily: fontFamily() }}>
                {t("remove_confirm_label")}
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export default StorageAndData;
